<template>
  <stihl-section :title="$t('dss.page.security.secPwdChange')" class="mt-5">

    <div id="info-fed-acc" class="alert fade show alert-warning" role="alert" v-if="user.federatedAccounts?.length">
      <span>{{ $t('dss.page.security.infoFederatedAccount') }}</span>
    </div>

    <div v-else>
      <group-input id="oldPass" class="mb-3"
                   type="password"
                   :label="$t('dss.label.oldPassword')"
                   :required="true"
                   autocomplete="off"
                   v-model="v$.form.oldPassword.$model"
                   :validation-errors="v$.form.oldPassword.$errors"
                   @keyup.enter="save"/>

      <group-input id="newPass" class="mb-3"
                   type="password"
                   :label="$t('dss.label.newPassword')"
                   :required="true"
                   autocomplete="new-password"
                   v-model="v$.form.newPassword.$model"
                   :validation-errors="v$.form.newPassword.$errors"
                   @keyup.enter="save"/>

      <group-input id="confirmPass" class="mb-3"
                   type="password"
                   :label="$t('dss.label.newPassword2')"
                   :required="true"
                   autocomplete="new-password"
                   v-model="v$.form.newPassword2.$model"
                   :validation-errors="v$.form.newPassword2.$errors"
                   @keyup.enter="save"/>

      <group-switch id="killSSOSwitch"
                    v-model="form.killSSO"
                    :label="$t('dss.page.setPW.killSSO')"/>

      <div class="buttons">
        <button id="save" type="button" class="btn btn-secondary" @click="save" :disabled="state==='loading'">
          {{ $t('dss.button.save') }}
        </button>
      </div>
    </div>

  </stihl-section>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import StihlSection from '@/components/ui/StihlSection.vue';
import GroupInput from '@/components/ui/GroupInput.vue';
import GroupSwitch from '@/components/ui/GroupSwitch.vue';
import useVuelidate from '@vuelidate/core';
import {
  hasDigit,
  hasLowercase,
  hasSpecial,
  hasUppercase,
  invalidCharacters,
  maxLength,
  minLength,
  notSameAsEmail,
  required,
  sameAs,
} from '@/components//validators';
import Backend, { RestError, UserDTO } from '@/components//backend';
import { AxiosError } from 'axios';

export default defineComponent({
  name: 'ChangePasswordSection',
  components: {
    StihlSection,
    GroupInput,
    GroupSwitch
  },
  props: {
    user: { type: Object as PropType<UserDTO>, required: true },
  },
  data() {
    return {
      form: {
        oldPassword: '',
        newPassword: '',
        newPassword2: '',
        killSSO: true,
      },
      state: 'ready' as 'ready' | 'loading',
    };
  },

  methods: {
    save() {
      if (this.state === 'loading') return; // must wait

      (this.v$ as any).form.$touch(); // trigger validation
      if (this.v$.$invalid) return console.error('validation failed', this.v$.$errors);

      this.state = 'loading';

      Backend.validateToken().then(api => api
          .changePassword({
            oldPassword: this.form.oldPassword,
            newPassword: this.form.newPassword,
            invalidateSessions: this.form.killSSO,
          })
          .then(_r => {
            this.$toast.info(this.$t('dss.page.security.msg.pwUpdated'));
          })
          .catch((e: AxiosError<RestError>) => {
            if (e.response?.data?.code === 'INVALID_PARAMETER' && e.response?.data?.invalidField === 'password') {
              return this.$toast.error(this.$t('dss.page.security.msg.invalidPassword'));
            } else if (e.response?.data?.code === 'CANNOT_REUSE_PASSWORD') {
              return this.$toast.error(this.$t('dss.page.security.msg.passwordAlreadyUsed'));
            }

            this.$toast.error(this.$t('dss.page.security.msg.pwUpdateError'));
          })
          .finally(() => this.state = 'ready')
      );
    },
  },

  setup: () => ({ v$: useVuelidate() }),
  validations() {
    return {
      form: {
        // must match the fields in 'this.form'
        oldPassword: {
          required,
          maxLength: maxLength(127)
        },
        newPassword: {
          required,
          minLength: minLength(12),
          maxLength: maxLength(127),
          hasLowercase,
          hasUppercase,
          hasDigit,
          hasSpecial: hasSpecial('! $ % & * - + = ; : , . " \' ? ~ # < > | § / @ ^ _ ( ) [ ] { }'),
          invalidCharacters,
          notSameAsEmail: notSameAsEmail(this.user.email),
        },
        newPassword2: {
          required,
          sameAsPassword: sameAs(this.form.newPassword)
        }
      }
    };
  },
});
</script>
